import { useCallback, useEffect, useState } from 'react'

import { Close, ExpandMore, InfoOutlined, Refresh, Tune } from '@mui/icons-material'
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, Skeleton, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import axios from 'axios'

import { featureKeys, featureSettingsService, supportsFeature, type FeatureKey, type FeatureRule, type FeatureSettings, type Platform } from '../../api/services/features'

import { defaultPlatformGate, parseRolloutPercentage, prepareFeatureLaunch, supportedPlatforms } from './feature-rollout'

const labels: Record<FeatureKey, string> = {
  dayResources: 'Day PDFs',
  sessionResults: 'Session results',
  sessionExitSurvey: 'Exit survey',
  equipmentDayOverride: 'Equipment removal',
  equipmentArtwork: 'Equipment images',
  profileRedesign: 'New profile',
  playerRedesign: 'New player',
  webOnboarding: 'Web buyer onboarding',
  appStoreOnboarding: 'App Store onboarding',
}
const descriptions: Record<FeatureKey, string> = {
  dayResources: 'Documents for each workout day',
  sessionResults: 'Summary after a completed workout',
  sessionExitSurvey: 'Feedback when a workout ends early',
  equipmentDayOverride: 'Remove equipment for a single day',
  equipmentArtwork: 'Illustrations in equipment lists',
  profileRedesign: 'Redesigned profile and settings, available in app 1.0.49+',
  playerRedesign: 'Redesigned workout player, available in app 1.0.49+',
  webOnboarding: 'Onboarding for web buyers on iOS 1.0.50+',
  appStoreOnboarding: 'Onboarding for App Store accounts on iOS 1.0.50+',
}
const platformNames: Record<Platform, string> = { ios: 'iOS', android: 'Android' }
const platforms = ['ios', 'android'] as const
const rowColumns = { xs: 'minmax(0, 1fr) 58px 40px', sm: 'minmax(0, 1fr) 110px 90px 40px' }
const unavailableRule: FeatureRule = { enabled: false, platforms: {}, rolloutBasisPoints: 0, cohortVersion: '1', internalUserIds: [] }

function audienceSummary(rule: FeatureRule) {
  const selected = platforms.filter(platform => rule.platforms[platform])
  if (!selected.length) return 'Select a platform in targeting'
  const summary = selected.map(platform => platformNames[platform] + ' ' + rule.platforms[platform]?.minVersion + '+').join(', ')
  return rule.internalUserIds.length ? summary + ', ' + rule.internalUserIds.length + ' test accounts' : summary
}

export default function FeatureFlagsForm() {
  const [settings, setSettings] = useState<FeatureSettings | null>(null)
  const [baseline, setBaseline] = useState<FeatureSettings | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [selectedFeature, setSelectedFeature] = useState<FeatureKey | null>(null)
  const [targetingDraft, setTargetingDraft] = useState<FeatureRule | null>(null)
  const [accountIdsInput, setAccountIdsInput] = useState('')
  const [percentageInputs, setPercentageInputs] = useState<Partial<Record<FeatureKey, string>>>({})
  const hasChanges = !!settings && !!baseline && JSON.stringify(settings.rules) !== JSON.stringify(baseline.rules)
  const changedFeatures = settings && baseline
    ? featureKeys.filter(key => JSON.stringify(settings.rules[key]) !== JSON.stringify(baseline.rules[key]))
    : []
  const hasInvalidPercentage = Object.values(percentageInputs).some(value => parseRolloutPercentage(value) === null)
  const defaultReason = changedFeatures.map(key => {
    const rule = settings?.rules[key]
    if (!rule) return ''
    const selectedPlatforms = platforms.filter(platform => rule.platforms[platform]).map(platform => platformNames[platform]).join(' + ') || 'no platforms'
    return labels[key] + ': ' + (rule.enabled ? rule.rolloutBasisPoints / 100 + '% on ' + selectedPlatforms : 'off')
  }).join('; ').slice(0, 500)

  const load = useCallback(async () => {
    setBusy(true)
    setError('')
    setSaved(false)
    try {
      const current = await featureSettingsService.get()
      setSettings(current)
      setBaseline(current)
      setReason('')
      setPercentageInputs({})
    } catch {
      setError('Could not load feature flags. Try reloading.')
    } finally {
      setBusy(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const change = (key: FeatureKey, update: Partial<FeatureRule>) => {
    setSaved(false)
    setSettings(current => current && supportsFeature(current, key) ? {
      ...current,
      rules: { ...current.rules, [key]: { ...current.rules[key], ...update } },
    } : current)
  }
  const prepareToggle = (key: FeatureKey, enabled: boolean) => {
    const rule = settings?.rules[key]
    if (!rule || busy) return
    change(key, enabled ? prepareFeatureLaunch(key, rule) : { enabled: false })
    setPercentageInputs(current => {
      const next = { ...current }
      delete next[key]
      return next
    })
  }
  const editPercentage = (key: FeatureKey, value: string) => {
    const percentage = parseRolloutPercentage(value)
    setSaved(false)
    setPercentageInputs(current => ({ ...current, [key]: percentage === null ? value : String(percentage) }))
    if (percentage !== null) change(key, { rolloutBasisPoints: percentage * 100 })
  }
  const discardChanges = () => {
    if (!baseline || busy) return
    setSettings(structuredClone(baseline))
    setPercentageInputs({})
    setReason('')
    setError('')
    setSaved(false)
  }
  const openTargeting = (key: FeatureKey) => {
    if (!settings?.rules[key]) return
    setSelectedFeature(key)
    setTargetingDraft(structuredClone(settings.rules[key]))
    setAccountIdsInput(settings.rules[key].internalUserIds.join(', '))
  }
  const closeTargeting = () => {
    setSelectedFeature(null)
    setTargetingDraft(null)
  }
  const updatePlatform = (platform: Platform, enabled: boolean) => {
    setTargetingDraft(current => {
      if (!current) return current
      const next = { ...current.platforms }
      if (enabled && selectedFeature) next[platform] = current.platforms[platform] ?? defaultPlatformGate(selectedFeature)
      else delete next[platform]
      return { ...current, platforms: next }
    })
  }
  const applyTargeting = () => {
    if (!selectedFeature || !targetingDraft) return
    change(selectedFeature, {
      ...targetingDraft,
      internalUserIds: accountIdsInput.split(',').map(id => id.trim()).filter(Boolean),
    })
    closeTargeting()
  }
  const save = async () => {
    if (!settings || !hasChanges || busy || hasInvalidPercentage) return
    setBusy(true)
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const committed = await featureSettingsService.save(settings, reason.trim() || defaultReason)
      const current = await featureSettingsService.get()
      setSettings(current)
      setBaseline(current)
      setReason('')
      setPercentageInputs({})
      if (current.configRevision !== committed.configRevision) {
        setError('Flags were saved, then another change followed. Review the current settings before editing again.')
        return
      }
      setSaved(true)
    } catch (failure) {
      const status = axios.isAxiosError(failure) ? failure.response?.status : undefined
      setError(status === 409
        ? 'Someone changed the flags while you were editing. Reload and review their changes before saving.'
        : 'Could not save flags. Check percentages, versions and test account IDs. If saving succeeded but confirmation failed, reload before retrying.')
    } finally {
      setBusy(false)
      setSaving(false)
    }
  }

  return <>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {saved && <Alert severity="success" sx={{ mb: 2 }}>Flags saved and confirmed.</Alert>}
    {settings && featureKeys.some(key => !supportsFeature(settings, key)) && <Alert severity="info" sx={{ mb: 2 }}>Some controls require a backend update. Available feature flags remain editable.</Alert>}
    <Card sx={{ maxWidth: 1040 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box>
          <Typography variant="h6">Feature rollout</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 700 }}>Turn on a feature to prepare a 100% rollout on its supported platforms. Adjust it if needed, then confirm below to apply.</Typography>
        </Box>
        <Tooltip title="Reload flags">
          <IconButton aria-label="Reload flags" disabled={busy} onClick={() => void load()} sx={{ width: 40, height: 40 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: rowColumns, gap: 1, px: { xs: 2, sm: 2.5 }, py: 1, bgcolor: 'background.default', borderBlock: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">Feature</Typography>
        <Typography variant="caption" color="text.secondary">Rollout</Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center">Enabled</Typography>
        <Box />
      </Box>

      {!settings && busy && <Stack spacing={2} sx={{ p: 2.5 }}>
        {featureKeys.map(key => <Skeleton key={key} variant="rounded" height={60} />)}
      </Stack>}

      {settings && featureKeys.map(key => {
        const supported = supportsFeature(settings, key)
        const rule = settings.rules[key] ?? unavailableRule
        const pending = changedFeatures.includes(key)
        const percentageInput = percentageInputs[key]
        const invalidPercentage = percentageInput !== undefined && parseRolloutPercentage(percentageInput) === null
        return <Box key={key} sx={{
          display: 'grid', gridTemplateColumns: rowColumns, gap: 1, alignItems: 'center',
          px: { xs: 2, sm: 2.5 }, py: 1.75, borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: pending ? 'action.hover' : 'background.paper',
        }}>
          <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={600}>{labels[key]}</Typography>
              {pending && <Chip size="small" variant="outlined" label="Pending" sx={{ height: 22, fontSize: 11 }} />}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
              {supported ? audienceSummary(rule) : 'Backend update required'}
            </Typography>
          </Box>
          <TextField
            disabled={busy || !supported} size="small" type="number" value={percentageInput ?? String(rule.rolloutBasisPoints / 100)}
            error={invalidPercentage} helperText={invalidPercentage ? 'Whole %: 0–100' : undefined}
            inputProps={{ min: 0, max: 100, step: 1, 'aria-label': labels[key] + ' rollout' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ '& input': { fontVariantNumeric: 'tabular-nums', px: 1.25 }, '& .MuiInputAdornment-root': { ml: 0.5 }, '& .MuiInputBase-root': { pr: 1 } }}
            onChange={event => editPercentage(key, event.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Switch disabled={busy || !supported} size="small" checked={rule.enabled} slotProps={{ input: { role: 'switch', 'aria-label': 'Enable ' + labels[key] } }} onChange={event => prepareToggle(key, event.target.checked)} />
          </Box>
          <Tooltip title={'Targeting for ' + labels[key]}>
            <IconButton aria-label={'Configure ' + labels[key]} disabled={busy || !supported} onClick={() => openTargeting(key)} sx={{ width: 40, height: 40 }}>
              <Tune fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      })}

      {hasChanges && <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Alert severity="info" sx={{ mb: 2 }}>Changes are prepared and are not live yet. Review the percentages and targeting, then confirm to apply.</Alert>
        <TextField
          disabled={busy} fullWidth size="small" label="Reason for change (optional)" placeholder={defaultReason}
          helperText="Leave blank to use the rollout summary."
          value={reason} inputProps={{ maxLength: 500 }} sx={{ mb: 2 }} onChange={event => setReason(event.target.value)}
        />
      </Box>}
    </Card>
    <Card component="footer" sx={{ maxWidth: 1040, position: 'sticky', bottom: 16, zIndex: 1, mt: 2, p: { xs: 2, sm: 2.5 }, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {hasChanges ? changedFeatures.length + ' ' + (changedFeatures.length === 1 ? 'feature has' : 'features have') + ' pending changes' : settings ? 'All changes saved. Revision ' + settings.configRevision : busy ? 'Loading flags…' : 'Flags unavailable'}
          </Typography>
          <Stack direction="row" spacing={1} flexShrink={0} justifyContent="flex-end">
            {hasChanges && <Button disabled={busy} onClick={discardChanges}>Discard</Button>}
            <Button disabled={busy || !hasChanges || hasInvalidPercentage} variant="contained" onClick={() => void save()}>
              {saving ? 'Applying…' : 'Confirm changes'}
            </Button>
          </Stack>
        </Stack>
    </Card>
    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mt: 2, maxWidth: 800 }}>
      <InfoOutlined sx={{ fontSize: 16, mt: 0.25, color: 'text.secondary' }} />
      <Typography variant="caption" color="text.secondary">Offline devices keep their last saved flags until they reconnect. Changes apply between workouts.</Typography>
    </Stack>

    <Dialog open={!!selectedFeature} onClose={closeTargeting} maxWidth="sm" fullWidth aria-labelledby="feature-targeting-title">
      <DialogTitle component="div" sx={{ pr: 7 }}>
        <Typography component="h2" variant="h6" id="feature-targeting-title">{selectedFeature ? labels[selectedFeature] : 'Feature targeting'}</Typography>
        <IconButton aria-label="Close targeting" onClick={closeTargeting} sx={{ position: 'absolute', right: 12, top: 12, width: 40, height: 40 }}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      {targetingDraft && selectedFeature && <DialogContent sx={{ pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{descriptions[selectedFeature]}</Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Supported app versions</Typography>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {supportedPlatforms(selectedFeature).map(platform => <Box key={platform} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '130px minmax(0, 1fr)' }, alignItems: 'center', gap: 2 }}>
            <FormControlLabel label={platformNames[platform]} control={<Switch checked={!!targetingDraft.platforms[platform]} onChange={event => updatePlatform(platform, event.target.checked)} />} />
            <Stack spacing={1}>
            <TextField
              size="small" fullWidth label="Minimum version" disabled={!targetingDraft.platforms[platform]}
              value={targetingDraft.platforms[platform]?.minVersion ?? ''}
              inputProps={{ 'aria-label': platformNames[platform] + ' minimum version' }}
              onChange={event => setTargetingDraft(current => {
                const target = current?.platforms[platform]
                return current && target ? { ...current, platforms: { ...current.platforms, [platform]: { ...target, minVersion: event.target.value } } } : current
              })}
            />
            <TextField
              size="small" fullWidth type="number" label="Minimum build" disabled={!targetingDraft.platforms[platform]}
              value={targetingDraft.platforms[platform]?.minBuildNumber ?? ''}
              inputProps={{ min: 0, step: 1, 'aria-label': platformNames[platform] + ' minimum build' }}
              onChange={event => setTargetingDraft(current => {
                const target = current?.platforms[platform]
                return current && target ? { ...current, platforms: { ...current.platforms, [platform]: { ...target, minBuildNumber: Number(event.target.value) } } } : current
              })}
            />
            </Stack>
          </Box>)}
        </Stack>
        <TextField
          fullWidth multiline minRows={2} size="small" label="Test account IDs" placeholder="Paste account IDs, separated by commas"
          value={accountIdsInput} onChange={event => setAccountIdsInput(event.target.value)}
          helperText="Up to 100 existing accounts. They bypass the rollout percentage, but still need a supported app version."
        />
        <Accordion disableGutters elevation={0} sx={{ mt: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, minHeight: 48 }}><Typography variant="body2" color="text.secondary">Advanced</Typography></AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <TextField fullWidth size="small" label="Rollout group" value={targetingDraft.cohortVersion} helperText="Keep this value to keep the same people in the rollout." onChange={event => setTargetingDraft(current => current ? { ...current, cohortVersion: event.target.value } : current)} />
          </AccordionDetails>
        </Accordion>
        <Typography variant="caption" color="text.secondary">Update the draft, then confirm from the feature list to apply.</Typography>
      </DialogContent>}
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={closeTargeting}>Cancel</Button>
        <Button variant="contained" onClick={applyTargeting}>Update draft</Button>
      </DialogActions>
    </Dialog>
  </>
}
